<template>
  <div class="triplea-login-wrapper">
    <div
      class="triplea-login-form-wrapper"
      v-if="!displayTransition || !isLoggedIn()">
      <h3 class="mb-8" style="display: flex; justify-content: center; font-weight: bold;">
        LOGIN
      </h3>

      <vs-input
        name="email"
        icon="icon icon-user"
        icon-pack="feather"
        :danger="emailStatus"
        danger-text="Please provide a valid email address"
        label-placeholder="Email"
        v-model.trim="email"
        class="w-full no-icon-border mb-10" />

      <vs-input
        name="password"
        :type="showPassword ? 'text' : 'password'"
        icon="icon icon-lock"
        icon-pack="feather"
        label-placeholder="Password"
        :danger="passwordStatus"
        danger-text="Please enter the correct password"
        v-model="password"
        class="w-full no-icon-border mb-2" />
      <label class="show-password">
        <input type="checkbox" v-model="showPassword" />
        Show password
      </label>
      <p v-if="credentials" class="warning-style">
        Please enter your credentials
      </p>

      <p v-if="loginError" class="error-style">
        {{ errorText ? errorText : 'Login Error. Please try again.' }}
      </p>

      <vs-button
        class="w-full mb-2 mt-6"
        data-click="passwordLogin()"
        @click="checkOTP()"
        id="passwordLoginBtn"
        ref="passwordLoginBtn">Login</vs-button>

      <div
        style="
            display: flex;
            justify-content: center;
            flex-direction: column;
            align-items: center;
          ">
        <small>Forgot Password ?
          <span
            style="color: #075593; cursor: pointer"
            @click="resetPassword()">Click here</span>
        </small>

        <span style="margin-top: 20px">
          Don't have an account yet ?
          <a href="https://triple-a.io/signup" target="_blank" rel="noopener">Click here</a>
        </span>
      </div>
    </div>
    <div
      class="triplea-logged-in-wrapper"
      v-if="displayTransition && isLoggedIn()">
      <h6 class="m-5 p-5">Successfully logged in.</h6>
    </div>
    <otp-prompt-for-login
      :email="email"
      :password="password"
      :active.sync="show2faOtpPrompt"
      otpRequired
      :login-request-data="loginRequestData"
      @verify="passwordLogin()" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import OtpPromptForLogin from '@/components/OtpPromptForLogin';

export default {
  name: 'LoginWithOTP',
  components: {
    OtpPromptForLogin,
  },
  props: {
    noRedirect: false,
    displayTransition: false,
  },
  data() {
    return {
      debugConsole: false,
      // clientId: 'oacid-ck79xd7c10000iensgs56dxch',
      clientId: process.env.VUE_APP_LOCAL_ENV ? process.env.VUE_APP_CLIENT_ID_LOCAL : process.env.VUE_APP_CLIENT_ID,
      email: '',
      password: '',
      emailStatus: false,
      passwordStatus: false,
      credentials: false,
      loginError: false,
      errorText: '',

      loginEmail: null,
      show2faOtpPrompt: false,
      showPassword: false,

      loginRequestData: null,
    };
  },
  computed: {},
  mounted() {
    window.addEventListener('keyup', this.pressEnterToLogin);
    window.addEventListener('load', this.removeOverlay);
    window.addEventListener('load', this.emptySelectedAddress);
    this.getRecaptchaToken();
  },
  beforeDestroy() {
    // removing listeners
    window.removeEventListener('keyup', this.pressEnterToLogin);
    window.removeEventListener('load', this.removeOverlay);
    window.removeEventListener('load', this.emptySelectedAddress);
  },
  methods: {
    ...mapActions(['getCurrentActiveEntityAdminProfile', 'getPermissionGroup']),

    getRecaptchaToken() {
      return this.$recaptchaLoaded().then(() => {
        return this.$recaptcha('login').then((token) => {
          return token;
        });
      });
    },

    removeOverlay() {
      this.$store.commit('TOGGLE_CONTENT_OVERLAY', false);
    },
    emptySelectedAddress() {
      this.$store.commit('UPDATE_SELECTED_ADDRESS', null);
    },
    resetPassword() {
      this.$store.commit('RESET_PASSWORD', true);
    },
    validateEmail(email) {
      let regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },

    pressEnterToLogin(e) {
      if (e.keyCode === 13) {
        this.checkOTP();
      }
    },

    passwordLogin() {
      if (!this.email || !this.clientId || !this.password) {
        this.credentials = true; //if no email and pwd is entered

        //if user has entered and decided to delete the username and password
        this.emailStatus = false;
        this.passwordStatus = false;

        return;
      } else {
        this.credentials = false;
        // start loading animation
        this.$vs.loading({
          container: this.$refs.passwordLoginBtn.$el,
          scale: 0.45,
        });
      }
      this.getRecaptchaToken().then((token) => {
        this.apiPasswordLogin(
          this.clientId,
          this.email,
          this.password,
          token
        ).then((oauthData) => {
          this.$vs.loading.close(this.$refs.passwordLoginBtn.$el);
          if (!oauthData) {
            this.loginError = true;
          }
          //Validate Email
          if (!this.validateEmail(this.email)) {
            this.emailStatus = true;
          } else {
            this.emailStatus = false;
          }
          if (oauthData.status === 400 || oauthData.status === 401) {
            this.errorText = `Incorrect email/password (${oauthData.status}). Please try again.`;
            this.passwordStatus = true;
            this.loginError = true;
            return;
          }
          else if (
            oauthData.status === 403 ||
            oauthData.status === 500
          ) {
            this.errorText = `Server error (${oauthData.status}). Please try again later, or contact our support team.`;
            this.loginError = true;
            this.passwordStatus = false;
            if (this.show2faOtpPrompt === true) {
              this.$vs.notify({
                title: 'Error',
                text: 'Invalid OTP',
                color: 'danger',
              });
            }
            return;
          } else {
            this.apiGetUserProfile().then(
              (profileData) => {
                this.show2faOtpPrompt = false;
                this.passwordStatus = false;
                this.loginError = false;

                // Save user token
                let userAccount = {
                  firstName: profileData.firstName || '',
                  lastName: profileData.lastName || '',
                  country: profileData.country || '',
                  mobile: profileData.mobile || '',
                  email: profileData.email || '',
                  clientId: this.clientId,
                  mfa_type: profileData.mfa_type || '',
                  //userToken: user_token,
                };

                // update entity admin twofa type
                this.$store.commit(
                  'UPDATE_ENTITY_ADMIN_2FA_TYPE',
                  userAccount.mfa_type
                );

                // TODO rewrite LOGIN_USER_ACCOUNT to keep token values unchanged if not specified (dont set to zero)
                this.$store.commit('LOGIN_USER_ACCOUNT', userAccount);

                if (this.noRedirect) {
                  // We don't want to redirect, so we don't take any action beyond logging in the user.
                  // We also avoid setting an active account, since it might confuse the sidebar while on the calling page.
                  if (this.debugConsole) console.debug('No redirect.');
                  return;
                }

                //Get merchants' data "owned" by the user profile
                this.apiGetMerchants()
                  .then((merchants) => {
                    //get the current user(entity admin) profile
                    this.getCurrentActiveEntityAdminProfile()
                      .then((currentEntityAdmin) => {
                        //check current user(entity admin) permission
                        let isAllowed = this.isUserAnAdmin(
                          currentEntityAdmin.data
                        );

                        if (isAllowed) {
                          //commit such that canSeeManageUserPage is true
                          this.$store.commit('UPDATE_IS_ADMIN', true);
                        } else {
                          this.$store.commit('UPDATE_IS_ADMIN', false);
                        }

                        this.apiGetAccounts()
                          .then((accounts) => {
                            this.$vs.loading.close(
                              this.$refs.passwordLoginBtn.$el
                            );

                            // Store state has been updated by our api, can use accounts with proper structure now.
                            accounts = this.$store.state.accounts;
                            if (
                              this.$store.state.accounts &&
                              this.$store.state.accounts.length > 0
                            ) {
                              if (this.debugConsole)
                                console.debug(
                                  'apiGetAccounts().then() : Found wallet(s). Setting active account to ',
                                  accounts[0].apiId
                                );
                              this.loginError = false;

                              // check the merchants which the user has access to :
                              // 1. 'group' property is an empty obj , means none (no permission)
                              // 2. 'group' property not an empty obj , means has accessed/permissions to the merchants.

                              // get merchants directly linked
                              let merchantsDirectLink = Object.values(
                                currentEntityAdmin.data.merchants
                              );

                              //get merchants under partner
                              let partners = Object.values(
                                currentEntityAdmin.data.partners
                              );
                              let merchantsUnderPartner = [];
                              for (let item of partners) {
                                for (let merchant of item.merchants) {
                                  merchantsUnderPartner.push(merchant);
                                }
                              }

                              // let merchantsFromCurrentEntityAdminData = merchantsDirectLink.concat(merchantsUnderPartner);
                              let merchantsFromCurrentEntityAdminData = [
                                ...merchantsDirectLink,
                                ...merchantsUnderPartner,
                              ].sort((a, b) => a.name > b.name);

                              for (let item of merchantsFromCurrentEntityAdminData) {
                                // means there is at least one merchant the user has accessed to, regardless of the permission
                                if (
                                  (item.group &&
                                    Object.keys(item.group).length > 0) ||
                                  (!item.group && item.id)
                                ) {

                                  //set the active Account or default account to be the account of the merchant that the user has access/permission to

                                  let findDefaultMerchant = merchants.data.find(
                                    (merchant) => merchant.id === item.id
                                  );
                                  if (this.debugConsole)
                                    console.log(
                                      'Find Default Merchant',
                                      findDefaultMerchant
                                    );

                                  let merchantAccounts =
                                    findDefaultMerchant.accounts;


                                  //Set the first main account in the merchantAccounts array as the default account when first login
                                  // let defaultAccount = accounts && accounts.find(element => element.apiId.indexOf('_t') < 0 && element.cryptoCurrency === "BTC");
                                  let defaultAccount =
                                    merchantAccounts &&
                                    merchantAccounts.find(
                                      (element) =>
                                        element.api_id.indexOf('_t') < 0 &&
                                        element.crypto_currency === 'BTC'
                                    );

                                  if (defaultAccount) {
                                    this.$store.commit(
                                      'SET_ACTIVE_ACCOUNT',
                                      defaultAccount.api_id
                                    );
                                  } else {
                                    // Has at least 1 account and if no main account exists in the accounts
                                    this.$store.commit(
                                      'SET_ACTIVE_ACCOUNT',
                                      merchantAccounts[0].api_id
                                    );
                                  }
                                  this.$router.push('/home');
                                  return;
                                } else {
                                  // No merchant access
                                  this.errorText = `No valid account found (401). Please contact your admin for access.`;
                                  this.loginError = true;
                                }
                              }

                              // //Set the first main account in the accounts array as the default account when first login
                              // let defaultAccount = accounts && accounts.find(element => element.apiId.indexOf('_t') < 0 && element.cryptoCurrency === "BTC");
                              // // console.log("Default Account", defaultAccount);
                              //
                              // if(defaultAccount)
                              // {
                              //   this.$store.commit("SET_ACTIVE_ACCOUNT", defaultAccount.apiId);
                              // }
                              // else
                              // {
                              //   // Has at least 1 account and if no main account exists in the accounts
                              //   this.$store.commit("SET_ACTIVE_ACCOUNT", accounts[0].apiId);
                              // }
                              // this.$router.push("/dashboard");
                              // return;
                            } else {
                              this.loginError = true;
                              this.errorText = 'Something went wrong (No valid account linked). Please contact support@triple-a.io.';
                              // Edge case, happens when request is successful but no accounts are returned. 
                              // Probably because the user has no accounts / not linked to any merchants.
                              return;
                            }
                          })
                          .catch((error) => {
                            console.log('Account error');

                            if (!this.noRedirect) {
                              // this.$router.push("/add-account");
                              return;
                            }
                          });
                      })
                      .catch((err) => {
                        console.log(
                          'Error in getting current active entity admin profile',
                          err
                        );
                      });

                    //get the list of permission groups
                    this.getPermissionGroup()
                      .then((res) => {
                        if (this.debugConsole)
                          console.log('Permission groups result', res);
                      })
                      .catch((err) => {
                        console.log('Error in getting permission groups', err);
                      });
                  })
                  .catch((error) => {
                    console.log(
                      'Error in receiving merchants data after log in',
                      error
                    );
                  });
              },
              (error) => {
                if (error.response.status === 401) {
                  if (error.response.data && error.response.data.message === 'no_permission_group_access') {
                    this.errorText = `No valid account found (${error.response.status}). Please contact your admin for access.`;
                    this.loginError = true;
                    this.passwordStatus = false;
                  } else {
                    this.errorText = `Incorrect email/password (${error.response.status}). Please try again.`;
                    this.passwordStatus = true;
                  }
                } else {
                  this.loginError = true;
                  this.errorText = `Something went wrong (${error.response.status}). Please try again later, or contact our support team.`;
                }
              }
            );
          }
        });
      });

    },
    checkOTP() {
      if (!this.email || !this.clientId || !this.password) {
        this.credentials = true; //if no email and pwd is entered

        //if user has entered and decided to delete the username and password
        this.emailStatus = false;
        this.passwordStatus = false;

        return;
      } else {
        let payload = {};
        payload.username = this.email;
        payload.password = this.password;
        if (this.debugConsole) console.log('Payload', payload);

        // this.show2faOtpPrompt = true;

        //call the request OTP API. If required, need to show the OTP popup. If not required, then immediately login
        this.checkLoginOTP(payload)
          .then((res) => {
            if (this.debugConsole) console.log('Login Req OTP result', res);

            if (res.data && res.data.required) {
              if (this.debugConsole) console.log('OTP is needed');
              this.loginRequestData = res.data;

              this.show2faOtpPrompt = true;
            } else {
              if (this.debugConsole) console.log('OTP is not needed');
              this.show2faOtpPrompt = false;
              //immediately call the password login function;
              this.passwordLogin();
            }
          })
          .catch((err) => {
            console.log('Error in Req OTP Result', err);
          });
      }
    },
  },
};
</script>

<style>
/* native class of vs-input with placeholder */
/* .vs-con-input-label.is-label-placeholder {
  margin-top: 25px;
} */
.vuesax-app-is-ltr .vs-input--input.hasValue+.vs-placeholder-label {
  /* Label of inputs when field is not empty */
  transform: translate(-3px, -75%) !important;
  -webkit-transform: translate(-3px, -75%) !important;
}

.show-password {
  display: block;
  font-size: smaller;
  margin-bottom: 12px;
}

.show-password>input {
  vertical-align: text-top;
}

.warning-style {
  font-size: 12px;
  font-weight: 500;
  color: orange;
}

.error-style {
  font-size: 12px;
  font-weight: 500;
  color: rgb(183, 58, 74); /* same as vs-color-danger */
  display: flex;
  justify-content: center;
}
</style>
